/**
 * Cell renderer for framed text in table cells
 * Renders text with a rounded rectangle border around it
 */

import type { CellRenderContext } from "./tableSchema";
import { PDF_TEXT_SIZE } from "./template";

interface CellBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Render framed text in a table cell
 * @param text - The text to display
 * @param cell - Cell bounds (x, y, width, height in points)
 * @param context - PDF rendering context
 */
export function renderFramedText(
  text: string | number | null | undefined,
  cell: CellBounds,
  context: CellRenderContext,
): void {
  if (text === null || text === undefined || text === "") {
    return;
  }

  const textStr = String(text);
  if (textStr.length === 0) {
    return;
  }

  const fontSize = context.fontSize ?? PDF_TEXT_SIZE;
  const { page, font, rgb } = context;
  const { x: cellX, y: cellY, width: cellWidth, height: cellHeight } = cell;

  // Calculate text dimensions
  const textWidth = font.widthOfTextAtSize(textStr, fontSize);
  // For text height, we use fontSize as the baseline-to-baseline distance
  // The actual visual height is typically around 0.7-0.8 * fontSize for most fonts
  const textHeight = fontSize * 0.8;

  // Padding around text (equal on all sides for proper centering)
  const paddingX = 3; // points
  const paddingY = 2; // points

  // Frame dimensions
  const frameWidth = textWidth + paddingX * 2;
  const frameHeight = textHeight + paddingY * 2;

  // Center the frame in the cell (both horizontally and vertically)
  const frameX = cellX + (cellWidth - frameWidth) / 2;
  // In PDF coordinates: cellY is top, cellY - cellHeight is bottom
  // Center of cell is at: cellY - cellHeight / 2
  // Frame should be centered around this point
  // Frame center Y = cellY - cellHeight / 2
  // Frame bottom Y (for drawRectangle) = center - frameHeight / 2
  const frameCenterY = cellY - cellHeight / 2;
  const frameBottomY = frameCenterY - frameHeight / 2;

  // Draw rounded rectangle frame
  // Use drawRectangle for proper positioning, then we'll add rounded corners manually
  // For now, use a simple rectangle - the rounded corners are a nice-to-have
  // The key is getting the positioning right
  page.drawRectangle({
    x: frameX,
    y: frameBottomY,
    width: frameWidth,
    height: frameHeight,
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
    color: undefined, // No fill
  });

  // Draw text centered in the frame (vertically and horizontally)
  const textX = frameX + paddingX;
  // Center text vertically in frame
  // Frame center is at: frameBottomY + frameHeight / 2
  // Text baseline should be slightly below center (adjust for font metrics)
  const textBaselineY = frameBottomY + frameHeight / 2 - fontSize / 3;

  page.drawText(textStr, {
    x: textX,
    y: textBaselineY,
    size: fontSize,
    font,
    color: rgb(0, 0, 0),
  });
}
